import React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { PageTitle } from '@/components/seo/page-title';
import { ScreenHeader } from '@/components/layout/screen-header';
import { Text } from '@/components/ui/text';
import { activePoliciesQueryOptions, policyContentQueryOptions } from '@/api/queries/policies';
import type { PolicyType } from '@/api/schemas/policy';
import { formatPolicyTitle } from '../utils/format-policy-title';
import { policyMarkdownStyles } from '../styles/policy-markdown-styles';
import { sanitizePolicyMarkdown } from '../utils/sanitize-policy-markdown';

interface PolicyViewerScreenProps {
  policyType: PolicyType;
}

export function PolicyViewerScreen({ policyType }: PolicyViewerScreenProps) {
  const title = formatPolicyTitle(policyType);

  const { data: policiesData, isLoading: isPoliciesLoading, error: policiesError } = useQuery(
    activePoliciesQueryOptions()
  );

  const policy = policiesData?.policies.find((p) => p.type === policyType) ?? null;
  const currentVersion = policy?.current_version ?? null;

  const { data: contentData, isLoading: isContentLoading } = useQuery({
    ...policyContentQueryOptions(policy?.id ?? '', currentVersion?.id ?? ''),
    enabled: !!policy?.id && !!currentVersion?.id,
  });

  const isLoading = isPoliciesLoading || (!!currentVersion && isContentLoading);

  return (
    <SafeAreaView className="flex-1 bg-background" testID="policy-viewer-screen">
      <PageTitle title={title} />
      <ScreenHeader title={title} showBackButton />

      {isLoading && (
        <View className="flex-1 items-center justify-center" testID="policy-viewer-loading">
          <ActivityIndicator size="large" />
        </View>
      )}

      {policiesError && !isLoading && (
        <View className="flex-1 items-center justify-center px-6" testID="policy-viewer-error">
          <Text className="text-muted-foreground text-center">
            Unable to load the {title.toLowerCase()}. Please try again later.
          </Text>
        </View>
      )}

      {!isLoading && !policiesError && !currentVersion && (
        <View className="flex-1 items-center justify-center px-6" testID="policy-viewer-no-content">
          <Text className="text-muted-foreground text-center">
            No {title.toLowerCase()} has been published yet.
          </Text>
        </View>
      )}

      {!isLoading && !policiesError && currentVersion && contentData && (
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 40 }}
          testID="policy-viewer-content"
        >
          <Text className="text-muted-foreground text-xs mb-4" testID="policy-viewer-version">
            Version {currentVersion.version_number} -- Effective {currentVersion.effective_date}
          </Text>
          <Markdown style={policyMarkdownStyles}>
            {sanitizePolicyMarkdown(contentData.content)}
          </Markdown>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}
